import React from "react";
import type { WinLossValueType } from "@src/types";
import { formatValueWithType, getColorClass, getDefaultDecimals } from "@src/utils/formatters";

type SizeType = "xs" | "s" | "m" | "l" | "xl";

interface Props {
  value: number | null | undefined;
  /** Threshold for win/loss coloring. If not provided, no coloring is applied (unless forceColor is set) */
  threshold?: number;
  /** Force a specific color regardless of value/threshold */
  forceColor?: "win" | "loss";
  /** Value type for formatting (number, currency, percentage, usdt, flat) */
  type?: WinLossValueType;
  /** Number of decimal places (ignored for "flat" type) */
  decimals?: number;
  /** Prefix a "+" on positive values (matches how PnL columns show gains). */
  showSign?: boolean;
  /** Render as badge with inverted colors (colored bg with white text) */
  badge?: boolean;
  /** Size variant: xs, s, m (default), l, xl */
  size?: SizeType;
  /** Additional CSS class names */
  className?: string;
}

export const ColoredValue: React.FC<Props> = ({
  value,
  threshold,
  forceColor,
  type = "number",
  decimals,
  showSign = false,
  badge = false,
  size = "m",
  className = "",
}) => {
  // Handle null/undefined values gracefully
  if (value === null || value === undefined) {
    const classes = ["ColoredValue", `size-${size}`, "text-muted"];
    if (className) classes.push(className);
    return <span className={classes.join(" ")}>-</span>;
  }

  const effectiveDecimals = decimals !== undefined ? decimals : getDefaultDecimals(type);
  const formattedValue =
    (showSign && value > 0 ? "+" : "") + formatValueWithType(value, type, effectiveDecimals);

  // Color determination
  const hasColor = forceColor !== undefined || threshold !== undefined;
  const isWin = forceColor === "win" || (threshold !== undefined && value >= threshold);

  // Build class list with component and size first
  const classes = ["ColoredValue", `size-${size}`];

  if (badge) {
    // Badge mode: pill with colored background
    classes.push("badge", "rounded-pill", "text-white");
    if (hasColor) {
      classes.push(isWin ? "bg-win-badge" : "bg-loss-badge");
    } else {
      classes.push("bg-secondary");
    }
  } else {
    // Standard mode: colored text
    classes.push(getColorClass(value, threshold, forceColor));
  }

  if (className) {
    classes.push(className);
  }

  return <span className={classes.filter(Boolean).join(" ")}>{formattedValue}</span>;
};
